import React, { useState } from 'react';
import { Wallet } from 'lucide-react';
import { useCashDrawer, openedBeforeToday } from './CashDrawerContext';
import CloseDrawerDialog from './CloseDrawerDialog';
import { formatClinicDate, formatClinicTime } from '../../utils/clinicTime';

/**
 * The cashier's drawer in the top bar. With no drawer open it offers to open one; with one open it
 * says since when, and opens the count and close. A drawer left open from an earlier day shows in
 * amber, so it gets closed before the new day's cash goes into it.
 *
 * `show` is for people who take money. Anyone with a drawer still open sees it regardless.
 */
const CashDrawerButton = ({ show = true }) => {
  const { active, loaded, drawer, askToOpen, setDrawer, refresh } = useCashDrawer();
  const [closing, setClosing] = useState(null);

  if (!active || !loaded) return null;
  if (!drawer && !closing && !show) return null;

  const stale = openedBeforeToday(drawer);
  const since = !drawer
    ? ''
    : stale
      ? formatClinicDate(drawer.openedAt, { day: 'numeric', month: 'short' }, '')
      : formatClinicTime(drawer.openedAt, { hour: 'numeric', minute: '2-digit', hour12: true }, '');
  const state = drawer?.counted ? 'Drawer counted' : 'Drawer open';

  return (
    <>
      {drawer ? (
        <button
          type="button"
          onClick={() => setClosing(drawer)}
          aria-label={`${state} since ${since}. Count and close it.`}
          className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
            stale
              ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
          }`}
        >
          <Wallet className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="hidden sm:block text-left leading-tight">
            <span className="block">{state}</span>
            <span className="block text-[11px] font-normal opacity-80">{`since ${since}`}</span>
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => askToOpen('manual')}
          aria-label="Open your cash drawer"
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <Wallet className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="hidden sm:inline">Open drawer</span>
        </button>
      )}

      {closing && (
        <CloseDrawerDialog
          drawer={closing}
          onClosed={() => setDrawer(null)}
          onClose={() => {
            setClosing(null);
            refresh();
          }}
        />
      )}
    </>
  );
};

export default CashDrawerButton;
